import { z } from "zod";

import {
  availabilityAppointmentTypeSchema,
  appointmentSlotSchema,
  dateFormatSchema,
  searchPatientsRequestSchema,
} from "@/shared/agent-api/requests";
import { normalizedEhrIdSchema } from "@/shared/common-schemas";
import { addressSchema } from "@/shared/models/address";
import { AppointmentType } from "@/shared/models/appointment";
import { dayOfWeekSchema, timePreferenceSchema } from "@/shared/models/scheduling-availability";

export const schedulingPatientLookupToolSchema = searchPatientsRequestSchema.omit({
  groupId: true,
  calledNumber: true,
  phoneNumber: true,
  includeCoverage: true,
});

export const schedulingBookedAppointmentsDateRangeToolSchema = z.object({
  startDate: dateFormatSchema.nullish(),
  endDate: dateFormatSchema.nullish(),
});

export const appointmentSlotToolSchema = appointmentSlotSchema.omit({ appointmentSlotActionId: true });

export const schedulingBookAppointmentToolSchema = z
  .object({
    appointmentSlot: appointmentSlotToolSchema,
  })
  .describe(
    "Book using six arguments only (same property names as each element of appointments[] from the last lookup) under appointmentSlot. Copy appointmentId, appointmentType, start, end, practitionerId, and locationId from the one chosen row’s JSON—never invent years, times, or short location codes."
  );

export const schedulingRescheduleBySlotIdsToolSchema = z.object({
  currentAppointmentId: normalizedEhrIdSchema,
  newAppointmentId: normalizedEhrIdSchema,
});

export const schedulingConfirmAppointmentToolSchema = z.object({
  appointmentId: normalizedEhrIdSchema,
});

export const schedulingOpenAppointmentLookupWindowToolSchema = z.object({
  practitionerIds: z
    .array(z.string())
    .describe("List of practitioner IDs from checkEligibility (use the practitionerId field for eligible providers)."),
  locationIds: z
    .array(z.string())
    .nullish()
    .describe(
      "Optional list of preferred location IDs. REQUIRED when referral context specifies a receiving locationId (pass as a single-element array) or when the patient asks for a specific location. Omit only on fallback steps that intentionally search all eligible locations."
    ),
  startDate: dateFormatSchema
    .nullish()
    .describe("Start date in yyyy-mm-dd format. Only provide if you need to search a specific date range."),
  endDate: dateFormatSchema
    .nullish()
    .describe("End date in yyyy-mm-dd format. Only provide if you need to search a specific date range."),
  timePreference: z
    .array(timePreferenceSchema)
    .nullish()
    .describe(
      'Time-of-day preference(s) the caller requires: "morning" (before 12 PM) and/or "afternoon" (12 PM or later). Set this ONLY when the caller clearly requires a time of day (e.g. "the earliest afternoon slot"). If they express only a soft or optional preference or say any time works (e.g. "afternoon\'s nice but anytime is fine"), OMIT it to avoid over-filtering; omitting searches all times of day.'
    ),
  daysOfWeek: z
    .array(dayOfWeekSchema)
    .min(1)
    .max(7)
    .nullish()
    .describe(
      "Weekdays requested by the caller, using ISO weekday numbers: 1 Monday, 2 Tuesday, 3 Wednesday, 4 Thursday, 5 Friday, 6 Saturday, 7 Sunday. Include every stated weekday even when the caller describes it as preferred; omit only when they have no weekday preference. Do not remove this filter unless the caller agrees to consider other days."
    ),
});

export const schedulingOpenAppointmentLookupToolParamsSchema = schedulingOpenAppointmentLookupWindowToolSchema.extend({
  appointmentType: availabilityAppointmentTypeSchema.default(AppointmentType.INITIAL_CONSULTATION),
});

export const schedulingRetrievePatientDemographicsToolSchema = z.object({});

export const schedulingUpdateCoverageSubscriberIdToolSchema = z.object({
  coverageId: z
    .string()
    .uuid()
    .nullish()
    .describe(
      "Coverage ID from retrievePatientDemographics. Omit it when retrievePatientDemographics returned no coverage on file — a new coverage will be created for the patient."
    ),
  subscriberId: z.string().trim().min(1).describe("Subscriber or member ID as stated by the patient."),
  payer: z.string().trim().min(1).describe("Insurance carrier or payer name as stated by the patient."),
  plan: z.string().trim().min(1).describe("Plan type as stated by the patient (for example PPO or HMO)."),
});

export const schedulingUpdatePatientProfileToolSchema = z
  .object({
    preferredPharmacy: z
      .string()
      .trim()
      .min(1)
      .nullish()
      .describe(
        "The patient's preferred/primary pharmacy as stated by the patient. Free text; may include the pharmacy name, location or cross-streets, and/or phone number. Omit when the patient did not provide one."
      ),
    physicianGivenName: z
      .string()
      .trim()
      .min(1)
      .nullish()
      .describe(
        "The given (first) name of the patient's current primary care physician (PCP) as stated by the patient. You are responsible for identifying which part of the name is the first name. Omit when the patient did not provide a PCP."
      ),
    physicianFamilyName: z
      .string()
      .trim()
      .min(1)
      .nullish()
      .describe(
        "The family (last) name of the patient's current primary care physician (PCP) as stated by the patient. You are responsible for identifying which part of the name is the last name. Omit when the patient did not provide a PCP."
      ),
  })
  .refine((d) => d.preferredPharmacy != null || d.physicianGivenName != null || d.physicianFamilyName != null, {
    message: "Provide at least one of preferredPharmacy, physicianGivenName, or physicianFamilyName",
  });

export const schedulingUpdatePatientAddressToolSchema = addressSchema
  .pick({ line1: true, line2: true, city: true, state: true, postalCode: true })
  .extend({
    line2: z
      .string()
      .nullable()
      .describe(
        "Apartment, unit, or suite. Pass null to clear a unit on file when the patient's corrected address has none. Omit only when updating other fields and the existing unit should stay."
      ),
  })
  .partial()
  .refine((d) => Object.values(d).some((v) => v !== undefined), {
    message: "Provide at least one address field to update",
  });
